import { useState } from "react";
import { useParams } from "react-router-dom";

import Container from "./Container";
import Input from "./Input";
import Alert, { ALERT_TYPE } from "./Alert";
import { resetPassword } from "../../utils/api";
import { EMPTY_FIELDS, PASSWORDS_MISMATCH } from "../../utils/constants";

import { ReactComponent as ForgotPasswordImage } from "../../assets/images/forgot.svg";
import { LockClosedIcon } from "@heroicons/react/solid";

function SetNewPassword() {
    const { resetPasswordToken } = useParams();

    const [alert, setAlert] = useState(null);
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [alertType, setAlertType] = useState(ALERT_TYPE.ERROR);

    const handleKeyPress = (key) => {
        if (key === "Enter") handleSetNewPassword();
    };

    const isInputValid = () => {
        if (password === "" || confirmPassword === "") {
            setAlert(EMPTY_FIELDS);
            setAlertType(ALERT_TYPE.ERROR);
            return false;
        }

        if (password !== confirmPassword) {
            setAlert(PASSWORDS_MISMATCH);
            setAlertType(ALERT_TYPE.ERROR);
            return false;
        }

        return true;
    };

    const handleSetNewPassword = async () => {
        if (isInputValid()) {
            try {
                await resetPassword(password, resetPasswordToken);
                setAlert("Your new password is set successfully");
                setAlertType(ALERT_TYPE.SUCCESS);
            } catch (error) {
                setAlertType(ALERT_TYPE.ERROR);
                setAlert(error.message);
            }
        }
    };

    return (
        <Container>
            <div className="w-full md:w-1/2 p-8">
                <div className="grid grid-flow-row gap-6">
                    <div className="grid-flow-row grid gap-4">
                        <div className="flex items-start justify-between">
                            <h1 className="font-semibold tracking-tight text-3xl text-indigo-900 text-left">
                                Create a password for your account
                            </h1>
                        </div>
                        <p className="text-left text-gray-500">Enter and confirm your password below</p>
                    </div>
                    <Alert text={alert} type={alertType} active={alert} close={() => setAlert("")} />
                    <div className="grid grid-flow-row gap-4">
                        <Input
                            id="password"
                            type="password"
                            name="Password"
                            placeholder="Enter password"
                            onChange={setPassword}
                            onKeyPress={handleKeyPress}
                            Icon={<LockClosedIcon className="h-4 w-4 text-gray-500" />}
                            value={password}
                        />
                        <Input
                            id="confirmPassword"
                            type="password"
                            name="Confirm Password"
                            placeholder="Confirm password"
                            onChange={setConfirmPassword}
                            onKeyPress={handleKeyPress}
                            Icon={<LockClosedIcon className="h-4 w-4 text-gray-500" />}
                            value={confirmPassword}
                        />
                    </div>
                    <div className="grid grid-flow-row gap-2">
                        <button
                            onClick={handleSetNewPassword}
                            className={
                                "block w-full px-3 py-3 " +
                                "bg-primary-600 hover:bg-primary-700 text-white rounded-md " +
                                "focus:bg-primary-700 font-medium focus:outline-none"
                            }
                        >
                            Create password
                        </button>
                        {/** Hidden for now to accommodate multi-client
                        <div className="flex justify-between items-end">
                            {/*TODO: Reactivate when ACE 2.0 releases are finished
                            <a href={"/login"} className="text-primary-600 font-medium text-sm">
                               Go to Login
                            </a>
                            
                            <a
                                href={"mailto:techsupport@psychpress.com.au"}
                                className="text-primary-600 font-medium text-sm"
                            >
                                Having trouble? Contact techsupport
                            </a>
                        </div> */}
                        <p className="text-gray-500 text-sm">
                            Once you have created your password, please return to your provided login link and enter
                            your username and new password.
                        </p>
                    </div>
                </div>
            </div>
            <div className="hidden md:block w-1/2 py-10 px-10 bg-gradient-to-r from-primary-50 to-secondary-50">
                <ForgotPasswordImage className="h-56 w-full sm:h-72 md:h-96 lg:w-full lg:h-full" />
            </div>
        </Container>
    );
}

export default SetNewPassword;
